import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import api from "../../utils/api";
import { userAtom } from "../../utils/store";

interface ApplicantStatusResponse extends Response {
  data: ApplicantStatusApiData;
}

interface ApplicantStatusApiData {
  token: string;
  expiration: string;
  role: string | null;
  amount: number | null;
  acceptanceFee: number | null;
  indigeneApplicationFee: number | null;
  internationalApplicationFee: number | null;
  applicationFee: number | null;
  lateInternationalApplicationFee: number | null;
  lateIndigeneApplicationFee: number | null;
  indigeneAcceptanceFee: number | null;
  internationalAcceptanceFee: number | null;
  applicationBatchId: number;
  applicationName: string;
  programId: number;
  programCode?: string;
  modeOfStudyId: number;
  modeOfStudyName: string;
  programName: string;
  isNewUser: boolean;
  email: string;
  applicantId: string;
  programTypeName: string;
  programTypeId: number;
  applicationNumber: string;
  isAdmin: boolean;
  fName: string;
  phoneNo: string;
  countryId?: number;
  lName: string;
  isPersonalInfoSubmitted: boolean;
  isqualificationSubmitted: boolean;
  isworkhistorySubmitted: boolean;
  iInstitutionSubmitted: boolean;
  isPaymentSubmitted: boolean;
  applicationStartDate: string;
  applicationEndDate: string;
  lateApplicationStartDate: string;
  lateApplicationEndDate: string;
  lateApplicationAmount: number | null;
  sessionId: number;
  earlyApplicationAmount: number | null;
  institutionShortName: string;
  mName: string | null;
  sessionName: string;
  applicantType: number;
  modeofEntry: string | null;
  applicantTypeName: string;
  applicationSource: number;
  modeOfEntryId: number | null;
  isDefaultPassword: boolean;
  specialization: string | null;
}

export interface ApplicantStatusData extends UserData {
  expiration?: string;
  role?: string | null;
  acceptanceFee?: number;
  indigeneApplicationFee?: number | null;
  internationalApplicationFee?: number | null;
  applicationFee?: number | null;
  lateInternationalApplicationFee?: number | null;
  lateIndigeneApplicationFee?: number | null;
  indigeneAcceptanceFee?: number | null;
  internationalAcceptanceFee?: number | null;
  modeOfStudyId?: number;
  modeOfStudyName?: string;
  applicantType?: number;
  applicantTypeName?: string;
  applicationSource?: number;
  modeOfEntryId?: number | null;
  isDefaultPassword?: boolean;
  isAdmin?: boolean;
  mName?: string | null;
}

interface UseGetApplicantDetailsQueryProps {
  applicantId?: string;
}

export const getApplicantDetailsQueryKey = (applicantId?: string) =>
  ["get-applicant-details", applicantId] as const;

const mapApplicantDetails = (
  fallbackUser: UserData | undefined,
  data: ApplicantStatusApiData,
): ApplicantStatusData => ({
  ...fallbackUser,
  ...data,
  token: data.token || fallbackUser?.token || "",
  applicantId: data.applicantId || fallbackUser?.applicantId || "",
  firstName: data.fName || fallbackUser?.firstName || "",
  lastName: data.lName || fallbackUser?.lastName || "",
  phoneNo: data.phoneNo || fallbackUser?.phoneNo || "",
  countryId: data.countryId ?? fallbackUser?.countryId,
  programType: data.programTypeName || fallbackUser?.programType || "",
  session: data.sessionName || fallbackUser?.session || "",
  modeOfEntry: data.modeofEntry || fallbackUser?.modeOfEntry,
  amount: data.amount ?? data.applicationFee ?? fallbackUser?.amount,
  acceptanceFee: data.acceptanceFee ?? fallbackUser?.acceptanceFee,
  earlyApplicationAmount:
    data.earlyApplicationAmount ?? fallbackUser?.earlyApplicationAmount,
  lateApplicationAmount:
    data.lateApplicationAmount ?? fallbackUser?.lateApplicationAmount,
  specialization: data.specialization ?? fallbackUser?.specialization,
});

const getApplicantDetails = async (applicantId: string) => {
  return (
    await api.get(`Authorization/applicantStatus?applicantId=${applicantId}`)
  ).data as ApplicantStatusResponse;
};

export const useGetApplicantDetailsQuery = (
  props?: UseGetApplicantDetailsQueryProps,
) => {
  const user = useAtomValue(userAtom);
  const applicantId = props?.applicantId ?? user?.applicantId;

  const query = useQuery({
    queryKey: getApplicantDetailsQueryKey(applicantId),
    queryFn: () => getApplicantDetails(applicantId as string),
    enabled: !!applicantId,
  });

  const applicantDetails = useMemo(
    () => (query.data?.data ? mapApplicantDetails(user, query.data.data) : user),
    [query.data, user],
  );

  return {
    ...query,
    applicantDetails,
  };
};

export default useGetApplicantDetailsQuery;
